import React from 'react';
import PropTypes from 'prop-types';
import { LicenseInfo } from '@mui/x-data-grid-pro';
import { LicenseInfo as PickersLicense } from '@mui/x-license-pro';
import _ from 'lodash';
import { create } from 'jss';
import { responsiveFontSizes } from '@mui/material';
import {
	createTheme,
	ThemeProvider as MuiThemeProvider,
	StyledEngineProvider,
} from '@mui/material/styles';
import { jssPreset, StylesProvider } from '@mui/styles';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import typography from './themeConf/typography';
import defaultThemeConfigs from './themeConf/index';
import { getCompanyTheme } from '../../context/ClientConfigContext';
import useSettings from '../../hooks/useSettings';

const baseConfig = {
	direction: 'ltr',
	typography,
	overrides: {
		MuiLinearProgress: {
			root: {
				borderRadius: 3,
				overflow: 'hidden',
			},
		},
		MuiListItemIcon: {
			root: {
				minWidth: 32,
			},
		},
	},
};

const LICENSE_KEY =
	'9e8f8d3e21a254605181e26bcd5db05fTz0xMDY1MTYsRT0xNzY5MjEyNzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=';

const createThemeProvider = ({ clientConfig } = {}) => {
	LicenseInfo.setLicenseKey(LICENSE_KEY);
	PickersLicense.setLicenseKey(LICENSE_KEY);

	const jss = create({ plugins: [...jssPreset().plugins] });
	const themeData = getCompanyTheme(clientConfig);

	const { themeConfigs: compThemeConfigs, themeOverrides: compThemeOverrides } =
		themeData;

	const themeConfigs = [...defaultThemeConfigs, ...compThemeConfigs];

	const createThemeFunc = (settings = {}) => {
		let themeConfig = themeConfigs.find(
			(themeRef) => _.lowerCase(themeRef.name) === _.lowerCase(settings.theme)
		);

		if (!themeConfig) {
			console.warn(new Error(`The theme ${settings.theme} is not valid`));
			themeConfig = _.head(themeConfigs);
		}

		let overrides = {};
		if (compThemeOverrides) {
			overrides = compThemeOverrides.find(
				(themeRef) => themeRef.name === settings.theme
			);
		}
		overrides = overrides || {};

		let theme = createTheme(
			_.merge({}, baseConfig, themeConfig, overrides, {
				direction: settings.direction,
			})
		);

		if (settings.responsiveFontSizes) {
			theme = responsiveFontSizes(theme);
		}

		return theme;
	};

	return (props) => {
		const { settings } = useSettings();
		const { children } = props;

		return (
			<StyledEngineProvider injectFirst>
				<MuiThemeProvider theme={createThemeFunc(settings)}>
					<StylesProvider jss={jss}>
						<LocalizationProvider dateAdapter={AdapterMoment}>
							{children}
						</LocalizationProvider>
					</StylesProvider>
				</MuiThemeProvider>
			</StyledEngineProvider>
		);
	};
};

createThemeProvider.defaultProps = {
	clientConfig: null,
};

createThemeProvider.propTypes = {
	clientConfig: PropTypes.objectOf(PropTypes.any),
};

export default createThemeProvider;
